// @ts-strict-ignore
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'src/store/store';
import CommunitySearchWrapper from 'src/modules/community/communitySearch/components/communitySearchWrapper';
import CommunitySearchSidebar from 'src/modules/community/communitySearch/components/communitySearchSidebar';
import { CommunityLayout } from 'src/components/layouts';
import { RequestError } from 'src/components/hoc/withErrorHandling';
import { getCommunitySearchSeo } from 'src/utils/seo';
import { fetchCurrentCommunity } from 'src/modules/shared/context/actions';
import { fetchSearchPosts, fetchSearchMembers, resetSearch } from 'src/modules/search/shared/actions';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import { PageContext, SearchType, SortType } from 'src/constants/types';

type Props = {
  page?: string;
  query?: string;
  searchType?: SearchType;
  sort?: SortType;
}
export default function CommunitySearchPage({
  page = '1',
  query = '',
  searchType = 'posts',
  sort = 'most-relevant',
}: Props) {
  const dispatch = useDispatch();
  const community = useSelector(({ context }) => context.currentCommunity);
  const userId = useSelector(({ context }) => context.currentUser?.userId);

  useEffect(() => {
    if (searchType === 'members' && !userId) {
      dispatch(showSignupModal({ community }));
    }
  }, [searchType, userId, community, dispatch]);

  useEffect(() => {
    if (!query) return;

    switch (searchType) {
      case 'members': {
        if (userId) {
          dispatch(fetchSearchMembers(query, page, community.communityId));
        }
        break;
      }
      case 'posts':
      default:
        dispatch(fetchSearchPosts(query, page, community.communityId, sort, true));
        break;
    }
    return () => {
      dispatch(resetSearch());
    };
  }, [query, page, sort, searchType, dispatch, community.communityId, userId]);

  return (
    <CommunityLayout
      currentPage="search"
      seo={getCommunitySearchSeo(community)}
      sideBarContent={<CommunitySearchSidebar query={query} searchType={searchType} />}
    >
      <CommunitySearchWrapper query={query} searchType={searchType} />
    </CommunityLayout>
  );
}

CommunitySearchPage.getInitialProps = async ({ store: { dispatch, getState }, query: { communitySlug, searchType, query, page, sort } }: PageContext) => {
  await dispatch(fetchCurrentCommunity(communitySlug));
  const state = getState();
  const { currentCommunity, communityErrorStatusCode, currentUser } = state.context;

  if (!currentCommunity) {
    const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
    if (isHiddenPrivateCommunity) {
      if (!currentUser) throw new RequestError('This page is not available to visitors', 401);
      throw new RequestError('This is a private community', 404); // user is not a member
    }
    throw new RequestError('Error fetching community', communityErrorStatusCode);
  }

  return {
    query,
    page,
    searchType,
    sort,
  };
};
